import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, timeAgo, openWS, mediaSrcById, translateMessages } from "../api";
import { offlineEnabled, mirrorConversations, readConversations, searchMirror } from "../lib/offlineSync";
import { useApp } from "../context/AppContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { useLongPress } from "../lib/longPress";
import { primeVoicePlayback } from "../lib/sounds";
import { Briefcase, Users, User, MessageSquarePlus, Search, UsersRound, ArrowLeft, Bell, BellOff, Trash2, Camera, Mic, FileText, MessageSquare, Video, Megaphone, Languages, UserPlus, Building2, Bot, StickyNote, WifiOff } from "lucide-react";
import { resizeToSquareJpeg } from "../lib/imageResize";
import { AvatarCropDialog } from "../components/AvatarCropDialog";
import { PersonalConnectBlock } from "../components/PersonalConnect";
import { JobPickerDialog } from "../components/StructuredDialogs";
import { ConfirmDeleteDialog } from "../components/Artifacts";

// Row wrapper so each conversation can have its own long-press hook instance
function RowShell({ onLongPress, children, ...props }) {
  const lp = useLongPress(onLongPress || (() => {}));
  const handlers = onLongPress
    ? {
        onTouchStart: lp.onTouchStart, onTouchMove: lp.onTouchMove, onTouchEnd: lp.onTouchEnd,
        onTouchCancel: lp.onTouchCancel, onClickCapture: lp.onClickCapture, onContextMenu: lp.onContextMenu,
        style: { WebkitTouchCallout: "none" },
      }
    : {};
  return <div {...props} {...handlers}>{children}</div>;
}

function initials(name) {
  return (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

const typeLabel = (m, t, tr) => {
  if (!m) return "";
  const content = tr || m.content;
  switch (m.type) {
    case "voice": return `\u{1F399} ${t("voice_message")}`;
    case "sticker": return `\u{1F3F7} ${t("sticker")}`;
    case "photo": return `\u{1F4F8} ${content || t("photo")}`;
    case "video": return `\u{1F3A5} ${content || t("video")}`;
    case "issue": return `\u{26A0} ${content}`;
    case "poll": return `\u{1F4CA} ${content || t("poll")}`;
    case "deleted": return `\u{1F6AB} ${t("deleted_message")}`;
    default: return content;
  }
};

const isJobGroup = (c) => c.type === "group" && !!c.job_id;

// Yellow highlight of the searched term inside a result snippet (same style as in-chat search)
function SnippetHighlight({ text, q }) {
  if (!text) return null;
  if (!q) return <>{text}</>;
  const esc = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${esc})`, "ig"));
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase() ? (
          <mark key={i} data-testid="global-search-highlight" className="bg-yellow-300 dark:bg-yellow-500/70 text-slate-900 dark:text-slate-950 rounded-sm px-0.5">
            {p}
          </mark>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        ))}
    </>
  );
}

// Pick the snippet that actually CONTAINS the match (content, translation,
// voice transcript, structured title or filename) so the highlight is visible
const pickSnippet = (m, q, lang) => {
  const ql = q.toLowerCase();
  const cands = [
    (m.translations?.[lang]?.content || "").trim(),
    m.content || "",
    (m.translations?.[lang]?.transcript || "").trim(),
    m.transcript || "",
    m.title || "",
    ...(m.media_filenames || []),
  ].filter(Boolean);
  return cands.find((c) => c.toLowerCase().includes(ql)) || cands[0] || "";
};

const resultTypeIcon = (type) => {
  if (type === "voice") return Mic;
  if (type === "document") return FileText;
  if (type === "video") return Video;
  return MessageSquare;
};

// ———— Supervisores ———— style separator (same look as the jobs date separators)
function RoleSectionSeparator({ label, testid }) {
  return (
    <div className="flex items-center gap-3 py-1.5 px-1" data-testid={testid}>
      <div className="h-px flex-1 bg-border" />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{label}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export default function MessagesPage() {
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [deleteConv, setDeleteConv] = useState(null); // conversation pending "delete for me" (admin)
  const [menuConv, setMenuConv] = useState(null); // long-press action sheet (mute / delete)

  const isMuted = (c) => Array.isArray(c?.muted_by) && c.muted_by.includes(user.id);

  const toggleMute = async (c) => {
    const next = !isMuted(c);
    // optimistic list update
    setConvs((prev) => prev.map((x) => x.id === c.id
      ? { ...x, muted_by: next ? [ ...(x.muted_by || []), user.id ] : (x.muted_by || []).filter((u) => u !== user.id) }
      : x));
    setMenuConv(null);
    try {
      await api.post(`/conversations/${c.id}/mute`, { user_id: user.id, muted: next });
      toast.success(next ? t("muted_on_toast") : t("muted_off_toast"));
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [trMap, setTrMap] = useState({});
  const [myCompany, setMyCompany] = useState(null); // for grouping convs by company

  useEffect(() => {
    api.get("/company/mine").then((r) => setMyCompany(r.data.company)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/conversations?user_id=${user.id}`);
      setConvs(res.data);
      setLoading(false);
      mirrorConversations(user.id, res.data);
    } catch (e) {
      // no signal → show the mirror kept on this phone
      if (offlineEnabled(user.id)) {
        const cached = await readConversations();
        if (cached.length) setConvs(cached.sort((a, b) => ((b.last_message?.created_at || b.updated_at || "") > (a.last_message?.created_at || a.updated_at || "") ? 1 : -1)));
      }
      setLoading(false);
    }
  }, [user.id]);

  // Previews follow the viewer's language (like the Home recent feed)
  useEffect(() => {
    const ids = convs
      .filter((c) => c.last_message?.id && c.last_message.type !== "voice" && (c.last_message.content || "").trim())
      .map((c) => c.last_message.id);
    if (ids.length === 0) return;
    let alive = true;
    translateMessages(ids, lang)
      .then((map) => { if (alive && map) setTrMap(map); })
      .catch(() => {});
    return () => { alive = false; };
  }, [convs, lang]);

  useEffect(() => {
    load();
    const close = openWS(`user_${user.id}`, () => load());
    window.addEventListener("osc-conversations-changed", load); // Buddy created/deleted a chat by voice
    return () => { close(); window.removeEventListener("osc-conversations-changed", load); };
  }, [load, user.id]);

  // People-first lists: job groups live ONLY in the "Grupos Trabalhos" tab
  let filtered = convs;
  if (filter === "all") filtered = convs.filter((c) => c.type !== "job" && !isJobGroup(c));
  else if (filter === "unread") filtered = convs.filter((c) => c.unread > 0 && c.type !== "job" && !isJobGroup(c));
  else if (filter === "dm") filtered = convs.filter((c) => c.type === "dm");
  else if (filter === "jobgroups") filtered = convs.filter((c) => isJobGroup(c));
  // Deep search: also match message CONTENT (original + cached translations +
  // voice transcripts) via the backend, so a term typed in PT finds messages
  // stored in EN and vice-versa. Debounced; keeps the full results so a
  // WhatsApp-style "Messages" section can list every matching message.
  const [deepHits, setDeepHits] = useState(null); // Set<conversation_id> | null
  const [deepResults, setDeepResults] = useState(null); // {messages, photos} | null
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepOffline, setDeepOffline] = useState(false);
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setDeepHits(null); setDeepResults(null); setDeepLoading(false); return undefined; }
    setDeepLoading(true);
    const timer = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}&user_id=${user.id}`)
        .catch(async (err) => {
          // no signal → search the offline mirror instead
          if (!offlineEnabled(user.id)) throw err;
          const hits = await searchMirror(q);
          return { data: { messages: hits.filter((m) => !(m.media || []).some((x) => x.kind === "photo" || x.kind === "image")), photos: hits.filter((m) => (m.media || []).some((x) => x.kind === "photo" || x.kind === "image")), offline: true } };
        })
        .then((res) => {
          setDeepOffline(!!res.data.offline);
          const ids = new Set();
          [...(res.data.messages || []), ...(res.data.photos || [])].forEach((m) => {
            if (m.conversation_id) ids.add(m.conversation_id);
          });
          setDeepHits(ids);
          setDeepResults({ messages: res.data.messages || [], photos: res.data.photos || [] });
        })
        .catch(() => { setDeepHits(null); setDeepResults(null); })
        .finally(() => setDeepLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search, user.id]);

  // Combined message results (text + media captions), most recent first
  const searchQ = search.trim();
  const searchingMode = searchQ.length >= 2;
  const msgResults = searchingMode && deepResults
    ? [...(deepResults.messages || []), ...(deepResults.photos || [])]
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        .slice(0, 25)
    : [];

  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.last_message?.content || "").toLowerCase().includes(q) ||
      (c.job?.job_number || "").includes(q) ||
      (deepHits && deepHits.has(c.id)));
  }
  const typeIcons = { dm: User, crew: Users, job: Briefcase, group: UsersRound, buddy: Bot, notes: StickyNote };

  // Unread totals per filter tab (sum of unseen messages in each category)
  const sumUnread = (list) => list.reduce((s, c) => s + (c.unread || 0), 0);
  const unreadTotals = {
    all: sumUnread(convs.filter((c) => c.type !== "job" && !isJobGroup(c))),
    dm: sumUnread(convs.filter((c) => c.type === "dm")),
    jobgroups: sumUnread(convs.filter((c) => isJobGroup(c))),
  };
  unreadTotals.unread = unreadTotals.all;

  const TabBadge = ({ count, testid }) => (
    count > 0 ? (
      <Badge data-testid={testid} className="ml-1.5 bg-primary text-primary-foreground text-[9px] px-1.5 min-w-[18px] h-4 justify-center">
        {count}
      </Badge>
    ) : null
  );

  return (
    <div className="flex-1 min-h-0 overflow-y-auto chat-wallpaper" data-testid="messages-page-wallpaper" style={{ "--chat-bg-url": `url('${user?.wallpaper_media_id ? mediaSrcById(user.wallpaper_media_id) : "/chat-bg.jpg"}')` }}>
    <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 relative">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-bold text-2xl tracking-tight">{t("messages")}</h1>
        <Button data-testid="new-chat-button" onClick={() => setContactsOpen(true)} size="sm">
          <MessageSquarePlus className="h-4 w-4 mr-1.5" /> {filter === "jobgroups" ? t("new_job_group_chat") : t("new_chat")}
        </Button>
      </div>

      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="messages-search-input"
          placeholder={t("search_convs_msgs")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="w-full flex justify-start gap-0.5 overflow-x-auto sm:w-auto sm:grid sm:grid-cols-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsTrigger className="shrink-0" value="all" data-testid="conv-filter-all">
            {t("all")}<TabBadge count={unreadTotals.all} testid="conv-filter-all-badge" />
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="unread" data-testid="conv-filter-unread">
            {t("unread")}<TabBadge count={unreadTotals.unread} testid="conv-filter-unread-badge" />
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="dm" data-testid="conv-filter-dm">
            {t("direct")}<TabBadge count={unreadTotals.dm} testid="conv-filter-dm-badge" />
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="jobgroups" data-testid="conv-filter-jobgroups">
            {t("job_groups")}<TabBadge count={unreadTotals.jobgroups} testid="conv-filter-jobgroups-badge" />
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {searchingMode && (
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1 pb-1.5" data-testid="search-section-conversations">
          {t("conversations")}
        </div>
      )}
      <div className="space-y-1" data-testid="conversation-list">
        {loading && [1, 2, 3].map((i) => <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />)}
        {(() => {
          if (loading) return null;
          const renderRow = (c) => {
          const Icon = typeIcons[c.type] || Users;
          return (
            <RowShell
              key={c.id}
              data-testid="conversation-row"
              onLongPress={() => setMenuConv(c)}
              onClick={() => {
                // Optimistic: clear the unread badge as soon as the conversation is opened
                setConvs((prev) => prev.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)));
                navigate(`/chat/${c.id}`);
              }}
              className="px-3 py-3 rounded-lg hover:bg-muted/60 cursor-pointer transition-colors flex items-center gap-3"
            >
              {c.type === "dm" ? (
                <Avatar className="h-11 w-11">
                  {c.other_user?.avatar_media_id && (
                    <AvatarImage src={mediaSrcById(c.other_user.avatar_media_id)} alt="" className="object-cover" />
                  )}
                  <AvatarFallback style={{ backgroundColor: c.other_user?.avatar_color || "#64748b", color: "#fff" }} className="text-xs font-semibold">
                    {initials(c.name)}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${isJobGroup(c) ? "bg-primary/10 text-primary" : "bg-accent/20 text-accent-foreground"}`}>
                  {c.type === "group" && c.avatar_media_id ? (
                    <img src={mediaSrcById(c.avatar_media_id)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[16px] sm:text-sm font-medium truncate">{c.name}</span>
                  {isJobGroup(c) && c.job && (
                    <Badge variant="outline" className="text-[9px] px-1.5 font-mono-plex text-primary border-primary/40 shrink-0" data-testid="conversation-job-badge">
                      #{c.job.job_number}
                    </Badge>
                  )}
                  {c.job?.status === "completed" && <Badge variant="secondary" className="text-[9px] px-1">{t("completed")}</Badge>}
                </div>
                {(() => {
                  let draft = "";
                  try { draft = localStorage.getItem(`osc_draft_${c.id}`) || ""; } catch (e) { /* ignore */ }
                  if (draft) {
                    return (
                      <div className="text-[14px] sm:text-xs line-clamp-1 mt-0.5" data-testid="conversation-draft-preview">
                        <span className="font-medium text-primary italic">{t("draft")}:</span>{" "}
                        <span className="text-muted-foreground italic">{draft}</span>
                      </div>
                    );
                  }
                  if (c.last_message) {
                    return (
                      <div className="text-[14px] sm:text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {/* Sender name only in GROUPS — in a DM the sender is obvious */}
                        {c.type !== "dm" && <span className="font-medium">{c.last_message.sender_name}: </span>}
                        {typeLabel(c.last_message, t, trMap[c.last_message.id]?.content)}
                      </div>
                    );
                  }
                  return c.type === "group" ? (
                    <div className="text-xs text-muted-foreground mt-0.5">{c.member_count} {t("members")}</div>
                  ) : null;
                })()}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-[11px] text-muted-foreground">{timeAgo(c.last_activity, lang)}</span>
                <div className="flex items-center gap-1.5">
                  {isMuted(c) && <BellOff data-testid="conversation-muted-icon" className="h-3.5 w-3.5 text-muted-foreground" />}
                  {c.unread > 0 && (
                    <Badge data-testid="conversation-unread-badge" className="bg-primary text-primary-foreground text-[10px] min-w-[20px] justify-center">
                      {c.unread}
                    </Badge>
                  )}
                </div>
              </div>
            </RowShell>
          );
          };
          // Direct tab: separate Supervisors from Installers (like the jobs date separators)
          const renderDmRoleSplit = (list) => {
            const supers = list.filter((c) => c.other_user?.role !== "installer");
            const installers = list.filter((c) => c.other_user?.role === "installer");
            return (
              <>
                {supers.length > 0 && <RoleSectionSeparator label={t("supervisors_section")} testid="conv-section-supervisors" />}
                {supers.map(renderRow)}
                {installers.length > 0 && <RoleSectionSeparator label={t("installers_section")} testid="conv-section-installers" />}
                {installers.map(renderRow)}
              </>
            );
          };
          // "All" and "Direct" tabs: group conversations by COMPANY
          // (my company first → partner companies → loose/personal contacts).
          if ((filter === "all" || filter === "dm") && !searchingMode) {
            const myCid = myCompany?.id || "__mine__";
            const myName = myCompany?.name || t("company");
            const sections = new Map();
            const push = (key, label, order, c) => {
              if (!sections.has(key)) sections.set(key, { key, label, order, items: [] });
              sections.get(key).items.push(c);
            };
            filtered.forEach((c) => {
              if (c.type === "dm") {
                const cid = c.other_user?.company_id;
                if (!cid) push("personal", t("personal_contacts"), 2, c);
                else if (cid === myCid) push(myCid, myName, 0, c);
                else push(cid, c.other_user?.company_name || t("partner_companies"), 1, c);
              } else {
                // crews / groups / job groups belong to MY company context
                push(myCid, myName, 0, c);
              }
            });
            const ordered = [...sections.values()].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
            const multi = ordered.length > 1;
            return ordered.map((s) => (
              <React.Fragment key={s.key}>
                {multi && <RoleSectionSeparator label={s.label} testid={`conv-section-company-${s.key}`} />}
                {filter === "dm" && s.key === myCid ? renderDmRoleSplit(s.items) : s.items.map(renderRow)}
              </React.Fragment>
            ));
          }
          if (filter === "dm" && !searchingMode) {
            return renderDmRoleSplit(filtered);
          }
          return filtered.map(renderRow);
        })()}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-10">
            {searchingMode ? t("search_no_results") : t("no_messages")}
          </div>
        )}
      </div>

      {/* Global search: matching MESSAGES across all conversations (WhatsApp style) */}
      {searchingMode && (
        <div className="mt-6" data-testid="global-message-results">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1 pb-1.5" data-testid="search-section-messages">
            {t("messages")}{!deepLoading && msgResults.length > 0 ? ` (${msgResults.length})` : ""}
          </div>
          {deepOffline && !deepLoading && (
            <div className="text-[11px] text-amber-700 dark:text-amber-400 px-1 pb-2 flex items-center gap-1.5" data-testid="search-offline-hint"><WifiOff className="h-3 w-3" />{t("search_offline_hint")}</div>
          )}
          {deepLoading && (
            <div className="space-y-1">
              {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />)}
            </div>
          )}
          {!deepLoading && msgResults.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-6" data-testid="global-message-no-results">
              {t("search_no_results")}
            </div>
          )}
          {!deepLoading && msgResults.map((m) => {
            const Icon = resultTypeIcon(m.type);
            const showThumb = m.media_id && (m.type === "photo" || m.type === "video");
            return (
              <div
                key={m.id}
                data-testid="global-message-result-row"
                onClick={() => navigate(`/chat/${m.conversation_id}?msg=${m.id}`)}
                className="px-3 py-2.5 rounded-lg hover:bg-muted/60 cursor-pointer transition-colors flex items-center gap-3"
              >
                {showThumb ? (
                  <img src={mediaSrcById(m.media_id)} alt="" loading="lazy" className="h-10 w-10 rounded-lg object-cover shrink-0 border" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[15px] sm:text-sm font-medium truncate">{m.conversation_name}</span>
                    <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo(m.created_at, lang)}</span>
                  </div>
                  <div className="text-[14px] sm:text-xs text-muted-foreground line-clamp-2 mt-0.5">
                    <span className="font-medium">{m.sender_name}:</span>{" "}
                    <SnippetHighlight text={pickSnippet(m, searchQ, lang)} q={searchQ} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Long-press action sheet: mute / (admin) delete */}
      <Dialog open={!!menuConv} onOpenChange={(v) => { if (!v) setMenuConv(null); }}>
        <DialogContent className="max-w-xs" data-testid="conversation-menu">
          <DialogHeader>
            <DialogTitle className="text-sm truncate">{menuConv?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Button
              variant="outline"
              data-testid="conversation-mute-btn"
              className="w-full justify-start h-11"
              onClick={() => toggleMute(menuConv)}
            >
              {menuConv && isMuted(menuConv)
                ? <><Bell className="h-4 w-4 mr-2" /> {t("unmute_notifications")}</>
                : <><BellOff className="h-4 w-4 mr-2" /> {t("mute_notifications")}</>}
            </Button>
            {menuConv && !isMuted(menuConv) && (
              <p className="text-[11px] text-muted-foreground leading-relaxed px-1">{t("mute_job_exception_hint")}</p>
            )}
            <Button
              variant="outline"
              data-testid="conversation-delete-btn"
              className="w-full justify-start h-11 text-destructive hover:text-destructive"
              onClick={() => { const c = menuConv; setMenuConv(null); setDeleteConv(c); }}
            >
              <Trash2 className="h-4 w-4 mr-2" /> {t("delete_conversation")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* "Apagar conversa" (admin): hides it from MY list only; reappears on new activity */}
      <AlertDialog open={!!deleteConv} onOpenChange={(v) => { if (!v) setDeleteConv(null); }}>
        <AlertDialogContent data-testid="delete-conversation-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_conversation")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConv?.name} — {t("delete_conversation_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-conversation-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-conversation-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                const c = deleteConv;
                setDeleteConv(null);
                if (!c) return;
                try {
                  await api.post(`/conversations/${c.id}/hide`, { user_id: user.id });
                  setConvs((prev) => prev.filter((x) => x.id !== c.id));
                  toast.success(t("conversation_deleted"));
                } catch (e) {
                  toast.error(t("something_wrong"));
                }
              }}
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating interpreter button (mobile) — opens Buddy's client mode.
          New conversations remain available via the header button above. */}
      <button
        data-testid="interpreter-fab"
        title={t("interp_open_btn")}
        onClick={() => {
          try { primeVoicePlayback(); } catch (e) { /* ignore */ }
          window.dispatchEvent(new Event("open-interpreter"));
        }}
        className="md:hidden fixed right-4 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center active:scale-95 transition-transform"
        style={{ bottom: "calc(max(env(safe-area-inset-bottom, 0px) + 6px, 12px) + 76px)" }}
      >
        <Languages className="h-6 w-6" />
      </button>

      <ContactsDialog open={contactsOpen} onClose={() => setContactsOpen(false)} jobLinkMode={filter === "jobgroups"} />
    </div>
    </div>
  );
}

/**
 * Contacts dialog: start a DM or create a group.
 * With jobId set (opened from a Job Hub) OR jobLinkMode (opened from the
 * "Job Groups" tab, where the job is picked inside the dialog):
 *  - selection flow with checkboxes: 1 person -> existing/new DM opened in the
 *    job's context (/chat/:id?job=jobId); 2+ people -> a Job Group (job_id set)
 */
export function ContactsDialog({ open, onClose, jobId = null, jobNumber = null, jobLinkMode = false }) {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [starting, setStarting] = useState(null);
  const [groupMode, setGroupMode] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [pickedJob, setPickedJob] = useState(null); // jobLinkMode: job chosen inside the dialog
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [groupPhotoB64, setGroupPhotoB64] = useState(null); // square jpeg base64 (preview + upload)
  const groupPhotoRef = useRef(null);
  const [groupCropFile, setGroupCropFile] = useState(null); // picked file → circular crop dialog

  // Selection flow: pick 1+ people (job context fixed or picked in-dialog)
  const pickFlow = !!jobId || jobLinkMode;
  const effJobId = jobId || pickedJob?.id || null;
  const effJobNumber = jobNumber || pickedJob?.job_number || null;

  const onPickGroupPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setGroupCropFile(file); // open the circular crop — result lands in groupPhotoB64
  };
  const [selected, setSelected] = useState([]);
  const [creating, setCreating] = useState(false);
  const [broadcastMode, setBroadcastMode] = useState(false);
  const [broadcastText, setBroadcastText] = useState("");
  const [requireAck, setRequireAck] = useState(false);
  const [bcUrgent, setBcUrgent] = useState(false);
  const [sendingBc, setSendingBc] = useState(false);
  const [personalOpen, setPersonalOpen] = useState(false); // "add loose contact" block
  const [deleteContact, setDeleteContact] = useState(null); // personal contact pending delete confirmation
  const [deleteHasConv, setDeleteHasConv] = useState(false); // active conversation warning inside the dialog

  // Trash on a personal contact: check for an active conversation first so the
  // confirmation can warn that the chat will also be deleted (for this user).
  const askDeleteContact = async (e, c) => {
    e.stopPropagation();
    try {
      const r = await api.get(`/contacts/${c.id}/status`);
      setDeleteHasConv(!!r.data.has_conversation);
    } catch (err) {
      setDeleteHasConv(false);
    }
    setDeleteContact(c);
  };

  const doDeleteContact = async () => {
    if (!deleteContact) return;
    try {
      await api.delete(`/contacts/${deleteContact.id}`);
      toast.success(t("deleted"));
      setDeleteContact(null);
      api.get("/users").then((res) => setUsers(res.data));
    } catch (err) {
      toast.error(t("something_wrong"));
    }
  };

  useEffect(() => {
    if (open) {
      api.get("/users").then((res) => setUsers(res.data));
      setGroupMode(false);
      setBroadcastMode(false);
      setBroadcastText("");
      setRequireAck(false);
      setBcUrgent(false);
      setPersonalOpen(false);
      setPickedJob(null);
      setJobPickerOpen(false);
      setGroupName(jobNumber ? `#${jobNumber} — ` : "");
      setSelected([]);
      setSearch("");
    }
  }, [open, jobNumber]);

  const startChat = async (contact) => {
    setStarting(contact.id);
    try {
      const res = await api.post("/conversations/dm", { user_id: user.id, other_user_id: contact.id });
      onClose();
      navigate(`/chat/${res.data.id}${effJobId ? `?job=${effJobId}` : ""}`);
    } finally {
      setStarting(null);
    }
  };

  // Selection flow confirm: 1 person -> DM in job context; 2+ -> Job Group
  const confirmPickFlow = async () => {
    if (selected.length === 0 || (jobLinkMode && !pickedJob)) return;
    if (selected.length === 1) {
      const c = users.find((u) => u.id === selected[0]);
      if (c) await startChat(c);
      return;
    }
    await createGroup();
  };

  const toggleSelected = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  // Broadcast: same text goes to each selected person as a NORMAL DM (no group)
  const sendBroadcast = async () => {
    if (!broadcastText.trim() || selected.length === 0) return;
    setSendingBc(true);
    try {
      const res = await api.post("/broadcast", {
        sender_id: user.id,
        recipient_user_ids: selected,
        content: broadcastText.trim(),
        require_ack: requireAck,
        urgent: bcUrgent,
      });
      toast.success(t("broadcast_sent").replace("{n}", String(res.data.sent)));
      onClose();
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally {
      setSendingBc(false);
    }
  };

  const createGroup = async () => {
    if (!groupName.trim() || selected.length === 0) return;
    setCreating(true);
    try {
      // optional group photo chosen during creation
      let avatarMediaId = null;
      if (groupPhotoB64) {
        try {
          const up = await api.post("/media", {
            data_base64: groupPhotoB64, content_type: "image/jpeg", kind: "image",
            category: "avatar", uploaded_by: user.id, filename: "group.jpg",
          });
          avatarMediaId = up.data.id;
        } catch (e) { /* group still gets created without the photo */ }
      }
      const res = await api.post("/conversations/group", {
        creator_id: user.id,
        name: groupName.trim(),
        participant_ids: selected,
        job_id: effJobId,
        avatar_media_id: avatarMediaId,
      });
      setGroupPhotoB64(null);
      onClose();
      navigate(`/chat/${res.data.id}`);
    } catch (e) {
      toast.error("Failed");
    } finally {
      setCreating(false);
    }
  };

  const q = search.toLowerCase();
  const filtered = users
    .filter((u) => u.id !== user.id)
    .filter((u) => !q || u.name.toLowerCase().includes(q) || (u.title || "").toLowerCase().includes(q) || (u.company_name || "").toLowerCase().includes(q));

  const roleGroups = [
    { role: "admin", label: t("admin") },
    { role: "supervisor", label: t("supervisor") },
    { role: "installer", label: t("crews") },
    { role: "office", label: t("office") },
    { role: "manager", label: t("manager") },
  ];

  // Crews sorted ascending by team (PM1, PM2, ... PM10); others by name
  const crewNum = (u) => {
    const m = (u.crew_id || "").replace("crew-", "").toUpperCase().match(/^([A-Z]+)\s*(\d+)$/);
    return m ? parseInt(m[2], 10) : Number.MAX_SAFE_INTEGER;
  };
  const sortUsers = (a, b) => crewNum(a) - crewNum(b) || a.name.localeCompare(b.name);

  // Contacts grouped by COMPANY: my company first (with role subsections),
  // then partner companies, then loose/personal contacts at the end.
  const me = users.find((x) => x.id === user.id);
  const myCid = me?.company_id || null;
  const companySections = (() => {
    const map = new Map();
    filtered.forEach((u) => {
      const key = u.company_id || "__personal__";
      if (!map.has(key)) map.set(key, { key, name: u.company_name || "", items: [] });
      map.get(key).items.push(u);
    });
    const mine = myCid ? map.get(myCid) : null;
    if (mine) map.delete(myCid);
    const personal = map.get("__personal__");
    map.delete("__personal__");
    const partners = [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
    const out = [];
    if (mine) out.push({ ...mine, mine: true });
    out.push(...partners);
    if (personal) out.push({ ...personal, name: t("personal_contacts"), personal: true });
    return out;
  })();
  const showCompanyHeaders = companySections.length > 1;

  const renderContact = (c) => (
    <div
      key={c.id}
      data-testid={`contact-row-${c.id}`}
      onClick={() => ((groupMode || broadcastMode || pickFlow) ? toggleSelected(c.id) : !starting && startChat(c))}
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/60 cursor-pointer transition-colors"
    >
      {(groupMode || broadcastMode || pickFlow) && (
        <Checkbox
          data-testid={`contact-check-${c.id}`}
          checked={selected.includes(c.id)}
          onCheckedChange={() => toggleSelected(c.id)}
          onClick={(e) => e.stopPropagation()}
        />
      )}
      <Avatar className="h-11 w-11">
        {c.avatar_media_id && (
          <AvatarImage src={mediaSrcById(c.avatar_media_id)} alt="" className="object-cover" />
        )}
        <AvatarFallback style={{ backgroundColor: c.avatar_color, color: "#fff" }} className="text-xs font-semibold">
          {initials(c.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{c.name}</div>
        <div className="text-xs text-muted-foreground">
          {c.title} {c.crew_id ? `· ${c.crew_id.replace("crew-", "").toUpperCase()}` : ""}
        </div>
      </div>
      <Badge variant="secondary" className="text-[10px] shrink-0">{t(c.role)}</Badge>
      {c.deletable_contact && !groupMode && !broadcastMode && !pickFlow && (
        <button
          type="button"
          data-testid={`contact-delete-btn-${c.id}`}
          title={t("delete")}
          onClick={(e) => askDeleteContact(e, c)}
          className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="contacts-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {(groupMode || broadcastMode) && (
              <button data-testid="group-mode-back" onClick={() => { setGroupMode(false); setBroadcastMode(false); }} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted">
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            {broadcastMode ? t("broadcast_title")
              : pickFlow ? (jobId ? t("new_conversation") : t("new_job_group_chat"))
              : groupMode ? t("new_group") : t("contacts")}
          </DialogTitle>
        </DialogHeader>

        {/* jobLinkMode: pick WHICH job this conversation/group is about */}
        {jobLinkMode && (
          <Button
            variant="outline"
            data-testid="pick-job-button"
            onClick={() => setJobPickerOpen(true)}
            className={`justify-start w-full ${pickedJob ? "border-primary/50 bg-primary/5" : ""}`}
          >
            <Briefcase className="h-4 w-4 mr-2 text-primary" />
            {pickedJob
              ? <span className="truncate">#{pickedJob.job_number} — {pickedJob.customer_name}</span>
              : t("choose_job")}
          </Button>
        )}

        {!groupMode && !broadcastMode && !pickFlow && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                data-testid="new-group-button"
                onClick={() => setGroupMode(true)}
                className="justify-start"
              >
                <UsersRound className="h-4 w-4 mr-2 text-primary" /> {t("new_group")}
              </Button>
              <Button
                variant="outline"
                data-testid="new-broadcast-button"
                onClick={() => setBroadcastMode(true)}
                className="justify-start"
              >
                <Megaphone className="h-4 w-4 mr-2 text-primary" /> {t("broadcast_title")}
              </Button>
            </div>
            {/* Loose/personal contact: connect by e-mail/phone code or share my QR */}
            <Button
              variant="outline"
              data-testid="add-personal-contact-btn"
              onClick={() => setPersonalOpen((v) => !v)}
              className={`justify-start w-full ${personalOpen ? "border-primary/50 bg-primary/5" : ""}`}
            >
              <UserPlus className="h-4 w-4 mr-2 text-primary" /> {t("add_personal_contact")}
            </Button>
            {personalOpen && (
              <PersonalConnectBlock onConnected={(convId) => { onClose(); navigate(`/chat/${convId}`); }} />
            )}
          </div>
        )}

        {broadcastMode && (
          <div className="space-y-1.5">
            <Textarea
              data-testid="broadcast-message-input"
              placeholder={t("broadcast_message_ph")}
              value={broadcastText}
              onChange={(e) => setBroadcastText(e.target.value)}
              rows={3}
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground">{t("broadcast_hint")}</p>
            {/* Mandatory "Entendi" — sender sees who confirmed */}
            <label
              data-testid="broadcast-require-ack"
              className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${requireAck ? "border-primary/50 bg-primary/5" : "hover:bg-muted/50"}`}
            >
              <Checkbox checked={requireAck} onCheckedChange={(v) => setRequireAck(!!v)} className="mt-0.5" data-testid="broadcast-require-ack-checkbox" />
              <span className="min-w-0">
                <span className="text-sm font-medium block">{t("require_ack")}</span>
                <span className="text-[11px] text-muted-foreground block mt-0.5">{t("require_ack_hint")}</span>
              </span>
            </label>
            {/* 🚨 Urgent — re-push while unread, alert the sender if never seen */}
            <label
              data-testid="broadcast-urgent"
              className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${bcUrgent ? "border-destructive/50 bg-destructive/5" : "hover:bg-muted/50"}`}
            >
              <Checkbox checked={bcUrgent} onCheckedChange={(v) => setBcUrgent(!!v)} className="mt-0.5" data-testid="broadcast-urgent-checkbox" />
              <span className="min-w-0">
                <span className="text-sm font-medium block">🚨 {t("broadcast_urgent")}</span>
                <span className="text-[11px] text-muted-foreground block mt-0.5">{t("broadcast_urgent_hint")}</span>
              </span>
            </label>
          </div>
        )}

        {(groupMode || (pickFlow && selected.length >= 2)) && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="group-photo-pick-btn"
              onClick={() => groupPhotoRef.current?.click()}
              aria-label={t("group_photo")}
              className="h-11 w-11 rounded-full shrink-0 overflow-hidden border-2 border-dashed border-muted-foreground/40 flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              {groupPhotoB64 ? (
                <img src={`data:image/jpeg;base64,${groupPhotoB64}`} alt="" className="h-full w-full object-cover" />
              ) : (
                <Camera className="h-5 w-5" />
              )}
            </button>
            <input ref={groupPhotoRef} type="file" accept="image/*" className="hidden" data-testid="group-photo-file-input" onChange={onPickGroupPhoto} />
            <Input
              data-testid="group-name-input"
              placeholder={t("group_name")}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              autoFocus
            />
          </div>
        )}

        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="contacts-search-input"
            placeholder={(groupMode || broadcastMode || pickFlow) ? t("select_people") : t("search_contacts")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="max-h-[45vh] overflow-y-auto -mx-2 px-2">
          {companySections.map((sec) => (
            <div key={sec.key} className="mb-2">
              {showCompanyHeaders && (
                <div className="flex items-center gap-2 px-1 pt-2 pb-1" data-testid={`contacts-company-section-${sec.key}`}>
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                    {!sec.personal && <Building2 className="h-3 w-3" />}{sec.name || t("company")}
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              {sec.mine
                ? roleGroups.map((g) => {
                    const groupUsers = sec.items.filter((u) => u.role === g.role).sort(sortUsers);
                    if (groupUsers.length === 0) return null;
                    return (
                      <div key={g.role} className="mb-1">
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-3 pt-2 pb-1">{g.label}</div>
                        {groupUsers.map(renderContact)}
                      </div>
                    );
                  })
                : [...sec.items].sort((a, b) => a.name.localeCompare(b.name)).map(renderContact)}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-8">—</div>
          )}
        </div>

        {broadcastMode && (
          <DialogFooter>
            <Button
              data-testid="broadcast-submit"
              onClick={sendBroadcast}
              disabled={sendingBc || !broadcastText.trim() || selected.length === 0}
              className="w-full"
            >
              <Megaphone className="h-4 w-4 mr-2" /> {t("broadcast_send")} {selected.length > 0 ? `(${selected.length})` : ""}
            </Button>
          </DialogFooter>
        )}
        {groupMode && !pickFlow && (
          <DialogFooter>
            <Button
              data-testid="create-group-submit"
              onClick={createGroup}
              disabled={creating || !groupName.trim() || selected.length === 0}
              className="w-full"
            >
              <UsersRound className="h-4 w-4 mr-2" /> {t("create_group")} {selected.length > 0 ? `(${selected.length})` : ""}
            </Button>
          </DialogFooter>
        )}
        {pickFlow && (
          <DialogFooter>
            <Button
              data-testid="pickflow-submit"
              onClick={confirmPickFlow}
              disabled={
                creating || !!starting || selected.length === 0
                || (jobLinkMode && !pickedJob)
                || (selected.length >= 2 && !groupName.trim())
              }
              className="w-full"
            >
              {selected.length >= 2
                ? <><UsersRound className="h-4 w-4 mr-2" /> {t("create_group")} ({selected.length})</>
                : <><MessageSquarePlus className="h-4 w-4 mr-2" /> {t("start_conversation")}</>}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>

      {/* jobLinkMode: job selection (installer sees own jobs; sup/admin all) */}
      <JobPickerDialog
        open={jobPickerOpen}
        onClose={() => setJobPickerOpen(false)}
        zClass="z-[300]"
        onPick={(j) => {
          setPickedJob(j);
          setJobPickerOpen(false);
          setGroupName(`#${j.job_number} — `);
        }}
      />

      {/* Circular crop for the group photo */}
      <AvatarCropDialog
        open={!!groupCropFile}
        file={groupCropFile}
        onClose={() => setGroupCropFile(null)}
        onCropped={(b64) => { setGroupPhotoB64(b64); setGroupCropFile(null); }}
      />

      {/* Delete a PERSONAL contact ('contato avulso') — one-sided removal */}
      <ConfirmDeleteDialog
        open={!!deleteContact}
        onClose={() => setDeleteContact(null)}
        onConfirm={doDeleteContact}
        title={`${deleteContact?.name || ""} — ${deleteHasConv ? `${t("delete_contact_has_conv")} ` : ""}${t("delete_contact_confirm")}`}
        t={t}
        testid="contact"
      />
    </Dialog>
  );
}
